// Leaderboard Service
// Uses JSONBin.io for persistent storage

import {
  fetchLeaderboardData as jsonBinFetchAll,
  getEntriesByGameType as jsonBinGetEntries,
  getGrandMasters as jsonBinGetGrandMasters,
  getPlayerBoard as jsonBinGetPlayerBoard,
  computeReigns,
  getScoreStanding as jsonBinGetScoreStanding,
  saveLeaderboardEntry as jsonBinSaveEntry,
} from './jsonBinService';
import { trackLeaderboardSubmission } from './analyticsService';

// Get leaderboard entries for a specific game type
export const getLeaderboard = async (gameType, limit = 50) => {
  try {
    const entries = await jsonBinGetEntries(gameType, limit);
    return entries;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }
};

// One row per player (best run) with all of their runs attached
export const getPlayerLeaderboard = async (gameType, limit = 50) => {
  try {
    return await jsonBinGetPlayerBoard(gameType, limit);
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }
};

// Who holds #1 on a board and for how long (null if the board is empty)
export const getReigns = async (board) => {
  try {
    const data = await jsonBinFetchAll();
    return computeReigns(data.entries || [], board);
  } catch (error) {
    console.error('Error fetching reign history:', error);
    return null;
  }
};

// Where a just-finished score would rank (null if it cannot be worked out)
export const getScoreStanding = async (gameType, score, totalTime) => {
  try {
    return await jsonBinGetScoreStanding(gameType, score, totalTime);
  } catch (error) {
    return null;
  }
};

// Totals for the homepage banner: how many runs have been posted and how many
// pirates hold a ranking. Returns nulls on failure so the banner can hide
// itself rather than render a zero.
export const getLeaderboardTotals = async () => {
  try {
    const data = await jsonBinFetchAll();
    return {
      plays: (data.entries || []).length,
      players: Object.keys(data.users || {}).length,
    };
  } catch (error) {
    return { plays: null, players: null };
  }
};

// Get Grand Masters - players with 11+ best in ALL 4 games
export const getGrandMasters = async () => {
  try {
    const grandMasters = await jsonBinGetGrandMasters();
    return grandMasters;
  } catch (error) {
    console.error('Error fetching Grand Masters:', error);
    return [];
  }
};

// Every score reaches the board through saveScore: all four games, and both the
// already-logged-in path and the username-modal path in each of them. Tracking
// here instead of in the game views is the only way the event count can match
// the board — five of the eight call sites never fired one, so GA saw 52
// submissions in the 30 days the board actually recorded 102.
//
// The id comes from the server's saved entry, so an event can only exist for a
// run that really landed, and the same run can never be counted twice.
const trackedEntryIds = new Set();

const trackSavedEntry = (result, entry) => {
  // Analytics must never be able to break a save. Nothing in here runs before
  // the score is stored, and anything it throws stops here.
  try {
    const saved = result && result.entry;
    if (!saved || !saved.id || trackedEntryIds.has(saved.id)) return;

    trackedEntryIds.add(saved.id);
    trackLeaderboardSubmission(
      saved.gameType || entry.gameType,
      saved.score !== undefined ? saved.score : entry.score,
    );
  } catch (error) {
    console.warn('Submission tracking skipped:', error);
  }
};

// Save a new score entry
export const saveScore = async (entry) => {
  try {
    const result = await jsonBinSaveEntry(entry);
    if (result && result.success) trackSavedEntry(result, entry);
    return result;
  } catch (error) {
    console.error('Error saving score:', error);
    return { success: false, error: error.message };
  }
};

// Get user's best score for a game type
export const getUserBest = async (username, gameType) => {
  const entries = await getLeaderboard(gameType);
  return entries.find(e => e.username === username) || null;
};

// Get all stats for a user
export const getUserStats = async (username) => {
  if (!username) return null;

  try {
    // Get best scores for each game type
    // Every run, not the top 50: a player whose best sat below the 50th run
    // used to see "Not played yet" (25 player-game bests on 2026-10-21).
    const ALL = Number.MAX_SAFE_INTEGER;
    const [characterEntries, imageEntries, devilfruitEntries, bountyEntries, grandMasters] = await Promise.all([
      getLeaderboard('character', ALL),
      getLeaderboard('image', ALL),
      getLeaderboard('devilfruit', ALL),
      getLeaderboard('bounty', ALL),
      getGrandMasters(),
    ]);

    // Find user's best in each category (lists are sorted best first; names match in any case)
    const me = String(username).toLowerCase();
    const mine = (e) => String(e.username || '').toLowerCase() === me;
    const characterBest = characterEntries.find(mine);
    const imageBest = imageEntries.find(mine);
    const devilfruitBest = devilfruitEntries.find(mine);
    const bountyBest = bountyEntries.find(mine);

    // Check if user is a Grand Master
    const grandMasterEntry = grandMasters.find(gm => gm.username === username);

    // Count total games played (all entries for this user)
    const allEntries = [...characterEntries, ...imageEntries, ...devilfruitEntries, ...bountyEntries];
    const userEntries = allEntries.filter(mine);

    return {
      username,
      characterBest: characterBest ? { score: characterBest.score, time: characterBest.totalTime, date: characterBest.date } : null,
      imageBest: imageBest ? { score: imageBest.score, time: imageBest.totalTime, date: imageBest.date } : null,
      devilfruitBest: devilfruitBest ? { score: devilfruitBest.score, time: devilfruitBest.totalTime, date: devilfruitBest.date } : null,
      bountyBest: bountyBest ? { score: bountyBest.score, time: bountyBest.totalTime, date: bountyBest.date } : null,
      totalGamesPlayed: userEntries.length,
      isGrandMaster: !!grandMasterEntry,
      grandMasterTier: grandMasterEntry?.tier || null,
      grandMasterIcon: grandMasterEntry?.tierIcon || null,
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return null;
  }
};

// Format time from seconds to MM:SS
export const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format date to relative time
export const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Unknown';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};
